import React, { useCallback, useRef, useState } from 'react';
import { collaboratorColor, exactSource, orderedLines } from './collaborationProtocol';
import useChoreographyCollaboration from './useChoreographyCollaboration';
import CollaborativeRecovery from './CollaborativeRecovery';
import { RECONNECTING_TOAST, RECOVERY_STATES, recoveryStateFor } from './collaborationRecovery';
import { analyseBlueprintNotes, projectChoreoCanvas } from './blueprintComposer';
import { emptySnapshot, RoutineCanvasEditor, snapshotFromCanvasScore } from './MusicChoreography';

// MCRO001-F0061 / T-MCRO-061 (MCRO001-D004 §10.3) case H: `showingSavedVersion`
// is set only while the canvas is displaying the routine's SAVED snapshot text
// because the live collaboration document has not arrived yet. The person must
// be able to tell "this is the saved version, not yet live for editing" from
// "this is live", and the owner has ruled the canvas chrome stays minimal — so
// this rides on the one existing save-state badge rather than a new surface.
//
// The in-progress wording is PRESERVED rather than replaced: #17 requirement
// 5(e) below exists precisely because a benign connection step read as an
// access restriction, and collapsing every pre-join phase to one flat label
// would undo that. The prefix adds the provenance of the text on screen; the
// suffix keeps the phase's own honest description of what is happening.
// MCRO001-F0148/F0150 (D004 §12.3, choreo issue #41) divergence helpers.
// `divergence` is the bootstrap payload's provenance slice:
// { draftUpdatedAt, baseRevisionId, lastPublishedAt }. Everything here is
// pure and locale-independent so tests can pin exact output. A `null`
// divergence yields '' — the pre-F0148-server degrade is the component's
// truthy-field gate combined with the hook's `|| null` normalisation, which
// keeps the badge at the plain phase label when the envelope carried no
// provenance.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const exactDate = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return '';
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
};

const relativeTime = (value, now = Date.now()) => {
  const saved = new Date(value).getTime();
  if (!value || Number.isNaN(saved)) return '';
  const seconds = Math.max(0, Math.round((now - saved) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;
  const weeks = Math.round(days / 7);
  if (days < 61) return `${weeks}w ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
};

// The visible one-line summary, e.g. "last saved 2w ago · not published since
// 16 Jul 2026" — or "never published" when the routine has never been
// published. Informational, never an error.
export function divergenceSummary(divergence, now = Date.now()) {
  if (!divergence) return '';
  const saved = relativeTime(divergence.draftUpdatedAt, now);
  const published = divergence.lastPublishedAt
    ? `not published since ${exactDate(divergence.lastPublishedAt)}`
    : 'never published';
  return saved ? `last saved ${saved} · ${published}` : published;
}

// The accessible detail carries the exact dates, not only relative ones.
export function divergenceDetail(divergence) {
  if (!divergence) return '';
  const saved = exactDate(divergence.draftUpdatedAt);
  const published = divergence.lastPublishedAt
    ? `not published since ${exactDate(divergence.lastPublishedAt)}`
    : 'never published';
  return saved ? `Draft saved ${saved}, ${published}.` : `Draft saved, ${published}.`;
}

export const statusLabel = (phase, pending, showingSavedVersion = false, divergence = null) => {
  if (showingSavedVersion) {
    if (phase === 'loading') return 'Saved version · opening…';
    if (phase === 'admitting') return 'Saved version · connecting…';
    if (phase === 'reconnecting' || phase === 'resyncing') return 'Saved version · reconnecting…';
    return 'Saved version · read-only';
  }
  if (phase === 'loading') return 'Opening…';
  // #17 requirement 5(e): 'admitting' (the socket handshake on EVERY normal
  // page load) fell through every branch below to the 'Read-only' default —
  // a plain, benign, in-progress connection step read as an access
  // restriction. Loading/connecting states must be visually AND verbally
  // distinct from action-required ones; this is that distinction restored.
  if (phase === 'admitting') return 'Connecting…';
  if (phase === 'saving' || pending) return 'Saving…';
  if (phase === 'offline') return 'Save needs attention';
  if (phase === 'reconnecting' || phase === 'resyncing') return 'Reconnecting…';
  if (phase === 'recovery') return 'Save needs attention';
  // MCRO001-F0149 (D004 §12.2, choreo issue #41): the live draft IS the save
  // layer — revisions need an explicit "Save revision" checkpoint and
  // publish is its own rotation — so the honest label is "Draft saved", not
  // bare "Saved". The divergence summary (F0150) rides along ONLY in this
  // live branch: no other phase may claim a saved state (F0152).
  if (phase === 'live') {
    const summary = divergenceSummary(divergence);
    return summary ? `Draft saved · ${summary}` : 'Draft saved';
  }
  return 'Read-only';
};

function initials(person) {
  const given = String(person?.initials || '').trim();
  if (given) return given.slice(0, 2).toUpperCase();
  return String(person?.displayName || '?').split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
}

function personLabel(person) {
  const name = String(person?.displayName || 'Someone');
  const count = Number(person?.clientInstanceCount) || 1;
  const state = String(person?.connectionState || 'online');
  return `${name}${count > 1 ? ` (${count} sessions)` : ''}${state !== 'online' && state !== 'connected' ? ` · ${state}` : ''}`;
}

const MAX_VISIBLE_PEOPLE = 5;

// The presence roll: one small overlapping circle per person in the draft room
// (the server presence stream already aggregates one row per user, so the same
// person in two windows is one circle with a session count in its tooltip).
// Each circle wears the same deterministic per-person colour as that editor's
// remote caret in the canvas (D004 §5.3); initials, the full-name tooltip and
// the accessible label keep identity from being colour-only. Presence carries
// only the opaque `avatarRef` (no resolvable photo in this application), so
// circles always render initials. Beyond five people the tail collapses into a
// "+N" circle that expands into a named list.
export function CollaborationPeople({ people = [] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = people.length > MAX_VISIBLE_PEOPLE ? people.slice(0, MAX_VISIBLE_PEOPLE) : people;
  const overflow = people.length > MAX_VISIBLE_PEOPLE ? people.slice(MAX_VISIBLE_PEOPLE) : [];
  return <div className="mc-collab-people" role="group" aria-label={`${people.length} ${people.length === 1 ? 'person' : 'people'} editing`}>
    {visible.map((person) => <span className="mc-collab-person" key={person.userId} title={personLabel(person)} aria-label={personLabel(person)}>
      <span className="mc-collab-avatar" aria-hidden="true" style={{ background: collaboratorColor(person.userId) }}>{initials(person)}</span>
    </span>)}
    {overflow.length > 0 && <span className="mc-collab-overflow">
      <button
        type="button"
        className="mc-collab-avatar mc-collab-more"
        aria-expanded={expanded}
        aria-label={`${overflow.length} more ${overflow.length === 1 ? 'person' : 'people'}`}
        title={overflow.map((person) => String(person.displayName || '')).join(', ')}
        onClick={() => setExpanded((value) => !value)}
      >+{overflow.length}</button>
      {expanded && <ul className="mc-collab-overflow-list">
        {overflow.map((person) => <li key={person.userId}>
          <i aria-hidden="true" style={{ background: collaboratorColor(person.userId) }} />{personLabel(person)}
        </li>)}
      </ul>}
    </span>}
  </div>;
}

// Rich per-entity values that the line grammar cannot express (practiceNote,
// scene/cue/formation data, timing overrides) live in the collaborative
// document's reviewedOverrides, keyed by the projection identity
// `section:<sourceLineId>` / `allocation:<sourceLineId>`. The server does not
// merge them into its parsed projection, so the document stays authoritative and
// this client merge mirrors what a future server-side merge would produce.
export function mergeReviewedOverrides(base, document, score) {
  const overrides = document?.reviewedOverrides || {};
  if (!Object.keys(overrides).length) return base;
  const lines = orderedLines(document);
  const lineIdAt = (sourceLine) => lines[Number(sourceLine) - 1]?.sourceLineId;
  const sections = (base.sections || []).map((section) => ({ ...section }));
  const blocks = (base.blocks || []).map((block) => ({ ...block }));
  (score?.sections || []).forEach((scoreSection, sectionIndex) => {
    const target = sections[sectionIndex];
    if (!target) return;
    const sectionOverride = overrides[`section:${lineIdAt(scoreSection.sourceLine)}`];
    if (sectionOverride?.value && typeof sectionOverride.value === 'object') Object.assign(target, sectionOverride.value);
    (scoreSection.allocations || []).forEach((allocation) => {
      const allocationOverride = overrides[`allocation:${lineIdAt(allocation.sourceLine)}`];
      if (!allocationOverride?.value || typeof allocationOverride.value !== 'object') return;
      const startCount = Number(target.startCount) + (Number(allocation.startRow) - 1) * 8;
      const block = blocks.find((item) => item.sectionId === target.id && Number(item.startCount) === startCount);
      if (block) Object.assign(block, allocationOverride.value);
    });
  });
  return { ...base, sections, blocks };
}

// Projects the authoritative collaborative line document into the same rich
// snapshot shape the Canvas always edited: the exact source text drives the
// grammar (`# label` sections, `## title` sequences, `[N]`/`[N x 8]`
// allocations) and reviewedOverrides supply the entity values the grammar
// cannot carry. Rehearsal, publish and demonstration contexts consume this
// snapshot unchanged.
export function projectCollaborativeSnapshot(document) {
  if (!document) return emptySnapshot();
  const body = exactSource(document);
  const score = projectChoreoCanvas(body);
  const base = score.sections.some(({ allocations }) => allocations.length > 0)
    ? snapshotFromCanvasScore(score)
    : analyseBlueprintNotes(body).snapshot;
  return { ...mergeReviewedOverrides(base, document, score), sourceDocument: { schemaVersion: 1, body } };
}

// Builds the entity.set operation for a rich per-entity value against the
// entity's current reviewedOverrides version; submit through the ordinary
// operation queue so admission, optimistic application, resync and recovery all
// apply unchanged.
export function entitySetOperations(document, entityId, value, reason) {
  const current = document?.reviewedOverrides?.[entityId];
  return [{ type: 'entity.set', entityId, expectedEntityVersion: Number(current?.entityVersion || 0), value, reason }];
}

// The engine mount: one collaboration session per routine, projected into the
// original RoutineCanvasEditor surface. There is no alternative editor and no
// capability switch — read-only is purely the engine's phase.
export default function CollaborativeCanvas({
  organisationId,
  routineId,
  userId,
  enabled = true,
  onStatus,
  snapshot,
  canEdit,
  capabilities,
  reviewDisplay,
  onChangeReviewDisplay,
  routineTitle,
  publishedRevisionId,
  displayedRevisionId,
  registerWorkspaceCapture,
  demonstrationBoundary,
  demonstrationPlaybackContext = null,
  viewingAs = '',
  onChangeViewingAs = null,
  onPublish = null,
  publishBusy = false,
  onRevisionSaved = null,
}) {
  const projectionRef = useRef({ document: null, snapshot: emptySnapshot() });
  const project = useCallback((document) => {
    if (projectionRef.current.document !== document || !document) {
      projectionRef.current = { document, snapshot: projectCollaborativeSnapshot(document) };
    }
    return projectionRef.current.snapshot;
  }, []);
  const handleStatus = useCallback((status) => {
    onStatus?.({ ...status, snapshot: project(status?.snapshot?.sourceDocument || null) });
  }, [onStatus, project]);
  const collaboration = useChoreographyCollaboration({ organisationId, routineId, userId, enabled, onStatus: handleStatus });
  // The rails and fallback score read the page's revision snapshot exactly as
  // the original editor did; the live projection stands in when none is given.
  const editorSnapshot = snapshot || project(collaboration.document);
  // MCRO001-F0061 / T-MCRO-061 (MCRO001-D004 §10.3): 'idle' means two different
  // things. It is the resting state of a session that is switched OFF
  // (`enabled === false`, and the unmount cleanup), where rendering nothing is
  // correct — and it is ALSO the state of every freshly mounted canvas for the
  // one committed frame before useChoreographyCollaboration's bootstrap effect
  // runs. Returning null for the second case is what made the FIRST painted
  // frame of a Canvas tab-return empty even though the saved text was already
  // in memory. Keep the null for a genuinely disabled session; render the saved
  // snapshot for a session that is merely still attaching.
  if (!enabled) return null;
  if (collaboration.phase === 'idle' && typeof editorSnapshot?.sourceDocument?.body !== 'string') return null;
  // #17 requirement 5(e): protectedOffline/encryptionCapableOffline start at
  // PLACEHOLDER defaults until collaborationQueueProtection() actually
  // resolves (~1-2s into every normal load) — rendering off those defaults
  // made this warning FLASH on every load whose real, resolved answer was
  // "protected", then disappear: a transient warning during normal loading,
  // not a true and actionable one. protectionKnown gates both warnings until
  // the real answer is in, so they now either persist (true) or never show.
  const showOfflineWarnings = collaboration.protectionKnown;
  // T-MCRO-067 (D004 §4.1.4): the edits-unsendable panel's premise is "some of
  // YOUR edits weren't saved yet" — it can only be true when a local edit
  // exists (non-empty pending queue, unrebased durable queue, a recovery-carried
  // annotated merge, or captured local text; the hook publishes this as
  // recovery.localEdits). A join with an empty local edit queue cannot hold a
  // merge conflict, so the panel is suppressed there and the session's accurate
  // read-only / save-needs-attention state remains — the internal code and
  // message stay on the recovery object untouched. Every other tier
  // (reconnecting, edit-rejected, signed_out, secure_connection) renders
  // unchanged: their copy makes no unsaved-local-edits claim.
  const recoveryIsUnsendable = Boolean(collaboration.recovery)
    && (collaboration.recovery.state || recoveryStateFor(collaboration.recovery.code)) === RECOVERY_STATES.UNSENDABLE;
  const showRecovery = Boolean(collaboration.recovery) && (!recoveryIsUnsendable
    || collaboration.pendingCount > 0 || collaboration.recovery.localEdits === true);
  // #19: these transient warnings/recovery/toast/undo surfaces used to sit
  // directly in the workspace's normal document flow, each mounting and
  // unmounting as collaboration state changed (recovery flapping with #18,
  // storage-protection resolution, undo messages). Every mount/unmount
  // reflowed every sibling below it — including RoutineCanvasEditor itself —
  // so the canvas (and the caret mid-keystroke) visibly jumped up/down. The
  // status overlay below is a SINGLE, ALWAYS-mounted container taken out of
  // normal flow (position: absolute, see MusicChoreography.css); its own
  // children still mount/unmount conditionally, but that can never move
  // RoutineCanvasEditor or anything else in the workspace, because the
  // overlay never participates in the flow those elements are laid out in.
  return <div className="mc-collab-workspace">
    <div className="mc-collab-status-overlay" aria-live="polite">
      {showOfflineWarnings && !collaboration.encryptionCapableOffline && <p className="mc-collab-warning">This browser cannot encrypt offline recovery. Changes remain origin-scoped but do not have server-equivalent protection.</p>}
      {showOfflineWarnings && collaboration.encryptionCapableOffline && !collaboration.protectedOffline && <p className="mc-collab-warning">Offline recovery cannot be stored durably in this browser, so unsaved changes only survive while this tab stays open. They remain origin-scoped but do not have server-equivalent protection.</p>}
      {(collaboration.phase === 'reconnecting' || collaboration.phase === 'resyncing') && !collaboration.recovery
        && <p className="mc-collab-toast" role="status" aria-live="polite">{RECONNECTING_TOAST}</p>}
      {showRecovery && <CollaborativeRecovery
        recovery={collaboration.recovery}
        recoveryText={collaboration.recoveryText || collaboration.exactText}
        routineId={routineId}
        onDiscard={collaboration.discard}
        onRetryNow={collaboration.retryNow}
        onResolveMerge={collaboration.resolveMerge}
        onAttemptMerge={collaboration.resync}
        readOnly={canEdit === false}
      />}
      {collaboration.undoMessage && <p className="mc-collab-warning" role="status">{collaboration.undoMessage}</p>}
    </div>
    <RoutineCanvasEditor
      key={routineId}
      collaboration={collaboration}
      snapshot={editorSnapshot}
      canEdit={canEdit}
      orgId={organisationId}
      routineId={routineId}
      revisionId={publishedRevisionId}
      displayedRevisionId={displayedRevisionId}
      capabilities={capabilities}
      reviewDisplay={reviewDisplay}
      onChangeReviewDisplay={onChangeReviewDisplay}
      routineTitle={routineTitle}
      registerWorkspaceCapture={registerWorkspaceCapture}
      demonstrationBoundary={demonstrationBoundary}
      demonstrationPlaybackContext={demonstrationPlaybackContext}
      viewingAs={viewingAs}
      onChangeViewingAs={onChangeViewingAs}
      onPublish={onPublish}
      publishBusy={publishBusy}
      onRevisionSaved={onRevisionSaved}
    />
    <div className="sr-only" aria-live="polite" aria-atomic="true">{collaboration.remoteAnnouncement}</div>
  </div>;
}
